import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaArrowLeft as FaBack } from 'react-icons/fa';
import { useSwipeable } from 'react-swipeable';
import Navbar from './Navbar';
import Footer from './Footer';
import { exhibitions } from './data/exhibitions';

const ExhibitionDetail = () => {
    const { id, type } = useParams();
    const navigate = useNavigate();
    const [currentImage, setCurrentImage] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [exhibition, setExhibition] = useState(null);
    const [isHovering, setIsHovering] = useState(false);

    useEffect(() => {
        // Find the exhibition based on type and id
        const foundExhibition = exhibitions[type]?.find(ex => ex.id === parseInt(id));

        if (foundExhibition) {
            setExhibition(foundExhibition);
        } else {
            // Handle not found case
            navigate('/exhibitions');
        }
    }, [id, type, navigate]);

    useEffect(() => {
        let interval;
        if (!isHovering && exhibition) {
            interval = setInterval(() => {
                nextImage();
            }, 3000);
        }
        return () => clearInterval(interval);
    }, [isHovering, exhibition]);

    const nextImage = () => {
        if (isTransitioning || !exhibition) return;
        setIsTransitioning(true);
        setCurrentImage(prev => (prev + 1) % exhibition.images.length);
        setTimeout(() => setIsTransitioning(false), 500);
    };

    const prevImage = () => {
        if (isTransitioning || !exhibition) return;
        setIsTransitioning(true);
        setCurrentImage(prev => (prev - 1 + exhibition.images.length) % exhibition.images.length);
        setTimeout(() => setIsTransitioning(false), 500);
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: nextImage,
        onSwipedRight: prevImage,
        preventDefaultTouchmoveEvent: true,
        trackMouse: true,
        delta: 10,
        swipeDuration: 500,
        touchEventOptions: { passive: false }
    });

    if (!exhibition) {
        return (
            <div className="min-h-screen bg-white text-gray-800 font-sans">
                <Navbar />
                <div className="pt-32 pb-16 px-4">
                    <div className="container mx-auto text-center">
                        <h1 className="text-2xl font-light mb-4">Loading...</h1>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white text-gray-800 font-sans">
            <Navbar />

            <section className="pt-32 pb-16 px-4">
                <div className="container mx-auto max-w-5xl">
                    <div
                        onClick={() => navigate('/exhibitions')}
                        className="flex items-center gap-2 text-gray-600 hover:text-[#052e5a] transition-colors duration-300 mb-8 cursor-pointer"
                    >
                        <FaArrowLeft /> Back to Exhibitions
                    </div>

                    <h1 className="text-4xl font-light mb-6" style={{ color: '#5e6a7d' }}>{exhibition.title}</h1>

                    <div className="flex items-center gap-4 text-gray-600 mb-8">
                        <div className="flex items-center gap-2">
                            <FaMapMarkerAlt />
                            <span className="font-light tracking-wide">{exhibition.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <FaCalendarAlt />
                            <span className="font-light tracking-wide">{exhibition.date}</span>
                        </div>
                    </div>

                    <div className="relative mb-12">
                        <div
                            {...swipeHandlers}
                            className="relative overflow-hidden rounded-lg aspect-video group touch-pan-y"
                            onMouseEnter={() => setIsHovering(true)}
                            onMouseLeave={() => setIsHovering(false)}
                        >
                            <div className="relative w-full h-full">
                                {exhibition.images.map((image, index) => (
                                    <img
                                        key={index}
                                        src={image}
                                        alt={`${exhibition.title} - Image ${index + 1}`}
                                        className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ${currentImage === index ? 'opacity-100' : 'opacity-0'}`}
                                        draggable="false"
                                    />
                                ))}
                            </div>
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <div
                                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 transform hover:scale-110 cursor-pointer"
                                    onClick={prevImage}
                                >
                                    <FaArrowLeft />
                                </div>
                                <div
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 transform hover:scale-110 cursor-pointer"
                                    onClick={nextImage}
                                >
                                    <FaArrowRight />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-center mt-4 space-x-2">
                            {exhibition.images.map((_, index) => (
                                <div
                                    key={index}
                                    onClick={() => {
                                        if (!isTransitioning) {
                                            setIsTransitioning(true);
                                            setCurrentImage(index);
                                            setTimeout(() => setIsTransitioning(false), 500);
                                        }
                                    }}
                                    className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${currentImage === index ? 'bg-[#052e5a] w-6' : 'bg-gray-300 hover:bg-gray-400'}`}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="grid gap-8">
                        <section className="bg-white/50 p-6 rounded-lg">
                            <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Overview</h3>
                            <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.overview}</p>
                        </section>

                        {type === 'international' && (
                            <>
                                <section className="bg-white/50 p-6 rounded-lg">
                                    <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Workshops</h3>
                                    <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.workshops}</p>
                                </section>

                                <section className="bg-white/50 p-6 rounded-lg">
                                    <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Supervision</h3>
                                    <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.supervision}</p>
                                </section>

                                <section className="bg-white/50 p-6 rounded-lg">
                                    <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Cultural Experience</h3>
                                    <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.culture}</p>
                                </section>

                                <section className="bg-white/50 p-6 rounded-lg">
                                    <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Exhibition</h3>
                                    <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.exhibition}</p>
                                </section>
                            </>
                        )}

                        {type === 'annual' && (
                            <>
                                <section className="bg-white/50 p-6 rounded-lg">
                                    <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Purpose and Impact</h3>
                                    <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.purposeAndImpact}</p>
                                </section>

                                <section className="bg-white/50 p-6 rounded-lg">
                                    <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Distinguished Guests</h3>
                                    <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.distinguishedGuests}</p>
                                </section>

                                <section className="bg-white/50 p-6 rounded-lg">
                                    <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Audience and Media</h3>
                                    <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.audience}</p>
                                </section>

                                <section className="bg-white/50 p-6 rounded-lg">
                                    <h3 className="text-2xl font-medium mb-4" style={{ color: '#052e5a' }}>Experience and Legacy</h3>
                                    <p className="text-gray-600 font-light tracking-wide leading-relaxed">{exhibition.mediaExperience}</p>
                                </section>
                            </>
                        )}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ExhibitionDetail; 